import { useCallback, useContext, useState } from "react";
import { DateTimeRenderer } from "../common/datetime/DateTimeFormatter";
import { EntityDetailProps, EntityDetailTableProps } from "../common/entity/EntityInterfaces";
import { toUppercase } from "../common/helperfunctions/stringFunctions";
import Detail from "../common/panels/Detail/Detail";
import { Table } from "../common/panels/Detail/DetailTable";
import { DetailsPageTopControlsView } from "../common/panels/misc/DetailsPageTopControlsView/DetailsPageTopControlsView";
import { eventIdToInfo } from "./AuditLog";
import { LucideIcon } from "../common/icon/LucideIcon";
import { GetPersons } from "../common/misc/EntityRenders/EntityRenderer";
import { NotAvailable, NotSet } from "../common/misc/UIconstants";
import { genericEntityConstants, GenericEntityConstantsEntities } from "../api/GenericConstants";
import { Button } from "../common/buttons/Button/Button";
import { IEntityMinimalModel } from "../api/GenericTypes";
import styles from "./AuditLogsDetail.module.css";
import { GenericEntityPreviewModal } from "../Entities/GenericComponents/common/GenericEntityPreviewModal";
import { useEntityDetail } from "../api/BaseEntityApi";
import { GenericEntity } from "../Entities/GenericComponents/GenericComponents";
import { SessionContext } from "../common/contexts/SessionContext";
import { LinkButton } from "../common/buttons/LinkButton/LinkButton";
import { getDetailLink, getIndexRoute } from "../main/Routing";
import { apiKeysConstants } from "../api/ApiKey";
import { AuditLog } from "../api/AuditLog";
import { LinkEntity } from "../common/misc/LinkEntity/LinkEntity";
import { useEntityPermissions } from "../common/permissions/useEntityPermissions";
import { customImportConstants } from "../api/CustomImports";
import { personsConstants } from "../api/Person";

export const AuditLogsDetailTable = ({
  entity,
  entityConstants,
  fieldLabels,
  showLinks = true,
}: EntityDetailTableProps<"auditLogs">) => {
  const eventInfo = eventIdToInfo(entity.eventId);
  const entityInfo = entity.entityType ? genericEntityConstants[entity.entityType] : null;
  const { route } = useContext(SessionContext);

  const { canReadIndex: canReadCustomImports } = useEntityPermissions({ entityTypeId: "customImports" });

  return (
    <>
      <Table noPadding nonFoldable>
        <Table.Head>Details</Table.Head>
        <Table.Body>
          <Table.Body.RowContent
            title={fieldLabels.timestamp}
            content={
              <div className={"container_label"}>
                <div className={"container_label_name"} style={{ fontWeight: 600 }}>
                  <DateTimeRenderer date={entity.timestamp} includeDate includeTime includeElapsed />
                </div>
                <div className={"container_label_id"}>
                  <span style={{ whiteSpace: "nowrap" }}>
                    {toUppercase(entityConstants.entitySingular)}-ID: {entity.id ?? NotAvailable}
                  </span>
                </div>
              </div>
            }
          />
          <Table.Body.RowContent
            title={fieldLabels.eventId}
            content={
              <div className={`label label-soft-${eventInfo.color}`}>
                {eventInfo.icon && <LucideIcon name={eventInfo.icon} />} {eventInfo.label}
              </div>
            }
          />
          <Table.Body.RowContent
            title={fieldLabels.user}
            content={
              entity.user ? (
                !!entity.user.name ? (
                  <div className="flex align-center gap-5" style={{ width: "100%" }}>
                    <GetPersons persons={[entity.user]} />
                  </div>
                ) : (
                  <div style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    <LucideIcon name="user" color="var(--primary)" />{" "}
                    <span>{`Deleted ${personsConstants.entitySingular} - ID: ${entity.user.id}`}</span>
                  </div>
                )
              ) : (
                NotSet
              )
            }
          />
          {!!entity.apiKey && (
            <Table.Body.RowContent
              title={fieldLabels.apiKey}
              content={
                entity.apiKey.name ? (
                  <LinkEntity
                    routeOverride={route(getIndexRoute(apiKeysConstants.frontendIndexRoute))}
                    entityConstants={apiKeysConstants}
                    property={entity.apiKey}
                  />
                ) : (
                  <div style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    <LucideIcon name="key" color="var(--primary)" />{" "}
                    <span>{`Deleted ${apiKeysConstants.entitySingular} - ID: ${entity.apiKey.id}`}</span>
                  </div>
                )
              }
            />
          )}

          {!!entity.metadataMapping && (
            <Table.Body.RowContent
              title={fieldLabels.metadataMapping}
              content={
                entity.metadataMapping.name ? (
                  <LinkEntity
                    entityConstants={customImportConstants}
                    property={entity.metadataMapping}
                    showLink={!!canReadCustomImports}
                  />
                ) : (
                  <div style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    <LucideIcon name="key" color="var(--primary)" />{" "}
                    <span>{`Deleted ${customImportConstants.entitySingular} - ID: ${entity.metadataMapping.id}`}</span>
                  </div>
                )
              }
            />
          )}

          {!!entityInfo && (
            <>
              <Table.Body.RowContent
                title={fieldLabels.entityType}
                content={
                  <div className="flex align-center gap-5">
                    <LucideIcon name={entityInfo.entityConstants.icon} color="var(--primary)" />
                    {toUppercase(entityInfo.entityConstants.entitySingular)}
                  </div>
                }
              />
            </>
          )}
        </Table.Body>
      </Table>

      {!!entity.payload && !!Object.keys(entity.payload).length && (
        <Table noPadding nonFoldable style={{ paddingBottom: "50px" }}>
          <Table.Head>Additional information</Table.Head>
          <Table.Body style={{ padding: "5px" }}>
            <pre style={{ width: "100%", padding: "10px", margin: 0 }}>{JSON.stringify(entity.payload, null, 2)}</pre>
          </Table.Body>
        </Table>
      )}

      {!!entity.entityType && (
        <Table noPadding nonFoldable style={{ paddingBottom: "50px" }}>
          <Table.Head>Affected {entityInfo?.entityConstants.entityPlural ?? "items"}</Table.Head>
          <Table.Body>
            {!!entity.entityIntRecords?.length && (
              <RecordTable entityType={entity.entityType} records={entity.entityIntRecords} eventId={entity.eventId} />
            )}
            {!!entity.entityLongRecords?.length && (
              <RecordTable entityType={entity.entityType} records={entity.entityLongRecords} eventId={entity.eventId} />
            )}
            {!!entity.entityStringRecords?.length && (
              <RecordTable
                entityType={entity.entityType}
                records={entity.entityStringRecords}
                eventId={entity.eventId}
              />
            )}
          </Table.Body>
        </Table>
      )}
    </>
  );
};

const PAGESIZE = 20;
const RecordTable = ({
  eventId,
  entityType,
  records,
}: {
  eventId: AuditLog["eventId"];
  entityType: GenericEntityConstantsEntities;
  records: IEntityMinimalModel<number>[] | IEntityMinimalModel<string>[];
}) => {
  const { route } = useContext(SessionContext);

  const [currentNumberRecordsShown, setCurrentNumberRecordsShown] = useState<number>(PAGESIZE);

  const entityInfo = genericEntityConstants[entityType];
  const eventIdAllowsDetails = eventId !== "EntityDelete" && eventId !== "ApiKeyDelete";

  const [selectedRecord, setSelectedRecord] = useState<number | string | null>(null);
  const { data: entity } = useEntityDetail<GenericEntity>(
    entityInfo.entityConstants.resource,
    selectedRecord ?? 0,
    undefined,
    {
      enabled: !!selectedRecord && eventIdAllowsDetails,
    }
  );

  const linkTo = useCallback(
    ({ entity, entityTypeId }: { entity: GenericEntity; entityTypeId: GenericEntityConstantsEntities }) => {
      switch (entityTypeId) {
        case "apiKeys":
          return route(getIndexRoute(apiKeysConstants.frontendIndexRoute));
        default:
          return route(getDetailLink(entityInfo.entityConstants.frontendIndexRoute, entity.id));
      }
    },
    [entityInfo.entityConstants.frontendIndexRoute, route]
  );

  if (!entityInfo) return null;
  return (
    <>
      <div style={{ width: "100%", overflow: "auto" }}>
        <table className={styles.RecordTable}>
          <thead>
            <tr>
              <th>{`${toUppercase(entityInfo.entityConstants.entitySingular)}-ID`}</th>
              <th>Name</th>
              <th>Version</th>
            </tr>
          </thead>
          <tbody>
            {records?.slice(0, currentNumberRecordsShown).map((value) => (
              <tr
                key={`${entityType}-${value.id}`}
                onClick={() => eventIdAllowsDetails && setSelectedRecord(value.id)}
                style={{
                  backgroundColor: value.id === selectedRecord ? "var(--primary-light)" : "",
                  cursor: eventIdAllowsDetails ? "pointer" : "default",
                }}
                title={
                  eventIdAllowsDetails
                    ? `Click to view details of ${entityInfo.entityConstants.entitySingular} with ID ${value.id}`
                    : undefined
                }
              >
                <td>{value.id}</td>
                <td style={{ width: "100%" }}>
                  <LucideIcon name={entityInfo.entityConstants.icon} color="var(--primary)" />{" "}
                  {value.name ?? `ID: ${value.id}`}
                </td>
                <td>{value.version ?? NotAvailable}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {records.length > currentNumberRecordsShown && (
          <div style={{ padding: "5px 0", width: "100%" }}>
            <Button
              style={{ width: "100%", justifyContent: "center" }}
              className="flex align-center gap-5 btn btn-soft-info btn-xs"
              onClick={() => setCurrentNumberRecordsShown((prevState) => prevState + PAGESIZE)}
            >
              <div>
                <LucideIcon name={currentNumberRecordsShown ? "chevron-down" : "chevron-right"} /> Show more
              </div>
            </Button>
          </div>
        )}
      </div>

      {!!entity && (
        <GenericEntityPreviewModal
          entityTypeId={entityType}
          entity={entity as GenericEntity}
          componentProps={{
            forceOpen: true,
            onCloseCallback: () => setSelectedRecord(null),
            controls: (
              <div
                className="flex align-center gap-5"
                style={{ width: "100%", justifyContent: "flex-end", paddingTop: "5px" }}
              >
                <Button className="btn btn-default" onClick={() => setSelectedRecord(null)}>
                  Close
                </Button>
                <LinkButton className="btn btn-primary" linkTo={linkTo({ entity, entityTypeId: entityType })}>
                  Open
                </LinkButton>
              </div>
            ),
          }}
        />
      )}
    </>
  );
};

export const AuditLogDetail = ({
  permissions,
  routes,
  entity,
  entityApi,
  entityConstants,
  fieldLabels,
  entityTypeId,
}: EntityDetailProps<"auditLogs">) => {
  return (
    <Detail
      head={
        <DetailsPageTopControlsView
          entityTypeId={entityTypeId}
          entity={entity}
          entityConstants={entityConstants}
          nameOverride={`ID: ${entity.id}`}
          hideHistoryModal
        />
      }
      mainStyle={{ padding: "0px 10px" }}
      main={
        <div className="flex col-nowrap">
          <AuditLogsDetailTable
            entity={entity}
            entityConstants={entityConstants}
            fieldLabels={fieldLabels}
            entityTypeId={entityTypeId}
          />
        </div>
      }
      sidebar={<></>}
      sidebarPosition="hidden"
    />
  );
};
